import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "~/store";
import {
  fetchOrderingConfig,
  setOrderDispatchOption,
  setOrderingServiceRef,
} from "~/store/actions";
import { setOrderDispatchTime } from "~/store/actions";
import {
  getSelectedDispatchTime,
  getOrderDispatchOption,
  getAvailableOrderDispatchOptions,
  getPickupActive,
  getDeliveryActive,
  getServiceActive,
  getMinimumAmount,
  getDeliveryFee,
  getDeliveryPrice,
  getOrderingIsLoading,
  getServiceRef,
  getTipsEnabledForDispatchType,
  getTipRatiosForDispatchType,
  getExternalPaymentEnabledForDispatchType,
} from "~/store/selectors";
import { OrderDispatchOption, Price, TimeSlot } from "~/types";
import { useDispatchTypeLabel } from "./language";

interface HookResult {
  dispatchOption: OrderDispatchOption | null;
  dispatchOptionLabel: string | null;
  setOrderingOptions: (
    option: OrderDispatchOption,
    slot: TimeSlot | null,
    serviceRef: string | null,
  ) => void;
  availableDispatchOptions: OrderDispatchOption[];
  timeSlot: TimeSlot | null;
  pickupActive?: boolean;
  deliveryActive?: boolean;
  serviceActive?: boolean;
  minAmount: number | null;
  deliveryFee: number | null;
  deliveryPrice: Price | null;
  loading: boolean;
  tipsEnabled: boolean;
  tipRatios: number[];
  defaultTipRatios: number[];
  externalPaymentEnabled: boolean;
}

export const defaultTipRatios = [0.1, 0.125, 0.15];

export function useOrderingOptions(locationRef: string): HookResult {
  const dispatch = useDispatch();

  const availableDispatchOptions = useSelector((s: AppState) =>
    getAvailableOrderDispatchOptions(s, locationRef),
  );

  // NOTE: Fallback to first available if none selected
  // We may not want this if we choose to always prompt the user when no option selected
  const dispatchOption = useSelector(
    (s: AppState) => getOrderDispatchOption(s, locationRef) || null,
  );

  const timeSlot = useSelector((s: AppState) => getSelectedDispatchTime(s));

  const pickupActive = useSelector((s: AppState) =>
    getPickupActive(s, locationRef),
  );
  const deliveryActive = useSelector((s: AppState) =>
    getDeliveryActive(s, locationRef),
  );
  const serviceActive = useSelector((s: AppState) =>
    getServiceActive(s, locationRef),
  );

  const currentServiceRef = useSelector((s: AppState) =>
    getServiceRef(s, locationRef),
  );

  const minAmount = useSelector((s: AppState) =>
    dispatchOption === "DELIVERY" ? getMinimumAmount(s, locationRef) : null,
  );
  const deliveryFee = useSelector((s: AppState) =>
    dispatchOption === "DELIVERY" ? getDeliveryFee(s, locationRef) : null,
  );
  const deliveryPrice = useSelector((s: AppState) =>
    dispatchOption === "DELIVERY" ? getDeliveryPrice(s, locationRef) : null,
  );

  const loading = useSelector(
    (s: AppState) => getOrderingIsLoading(s, locationRef) || false,
  );

  useEffect(() => {
    locationRef && dispatch(fetchOrderingConfig(locationRef));
  }, [dispatch, locationRef]);

  const setOrderingOptions = useCallback(
    (
      option: OrderDispatchOption,
      slot: TimeSlot | null,
      serviceRef: string | null,
    ) => {
      // TODO: Do we need to allow setting back to null?
      if (option && option !== dispatchOption) {
        dispatch(setOrderDispatchOption(option));
      }

      dispatch(setOrderDispatchTime(slot));

      // NOTE: This loads the menu and location ordering config
      // which then in turn affects available dispatch options and time slots
      // Seems kind of infinite loopy to me
      // dispatch(fetchOrderingConfig(locationRef));

      if (
        option === "SERVICE" &&
        serviceRef &&
        currentServiceRef !== serviceRef
      ) {
        dispatch(setOrderingServiceRef(serviceRef, locationRef));
      }
    },
    [currentServiceRef, dispatch, dispatchOption, locationRef],
  );

  const { label: dispatchOptionLabel } = useDispatchTypeLabel(dispatchOption);

  const tipsEnabled = useSelector(
    (s: AppState) =>
      getTipsEnabledForDispatchType(s, locationRef, dispatchOption) || false,
  );

  const tipRatios = useSelector(
    (s: AppState) =>
      getTipRatiosForDispatchType(s, locationRef, dispatchOption) || [],
  );

  const externalPaymentEnabled = useSelector(
    (s: AppState) =>
      getExternalPaymentEnabledForDispatchType(
        s,
        locationRef,
        dispatchOption,
      ) || false,
  );

  return useMemo(
    () => ({
      dispatchOption,
      dispatchOptionLabel,
      setOrderingOptions,
      availableDispatchOptions,
      timeSlot,
      pickupActive,
      deliveryActive,
      serviceActive,
      minAmount,
      deliveryFee,
      deliveryPrice,
      tipsEnabled,
      tipRatios,
      defaultTipRatios,
      externalPaymentEnabled,
      loading,
    }),
    [
      availableDispatchOptions,
      deliveryActive,
      deliveryFee,
      deliveryPrice,
      dispatchOption,
      dispatchOptionLabel,
      loading,
      minAmount,
      pickupActive,
      serviceActive,
      setOrderingOptions,
      timeSlot,
      tipRatios,
      tipsEnabled,
      externalPaymentEnabled,
    ],
  );
}
